import {useEffect, useRef, useState} from 'react'
import {fromEntries, keys} from 'remeda'
import {useMemoValue} from './useMemo'

/** returns id's matched to dom elements. Keeps up to date with structural changes to the dom */
export const useElements = <T extends HTMLElement>(
  ids: string[],
  options?: {getEl?: (id: string, parentEl: T | HTMLElement) => Element | null | undefined;}
) => {
  const [elById, setElements] = useState<Record<string, T | undefined>>({})
  const ref = useRef<T>(null)
  const parentEl = ref.current ?? (typeof window != 'undefined' ? document.body : undefined)
  const getEl =
    options?.getEl ??
    ((id, parentEl) => parentEl?.querySelector(`#${id.replace(/(^|\s)(\d)/g, '$1\\3$2 ')}`) ?? undefined)
  useEffect(() => {
    if (!parentEl) return
    const fetchElements = () => {
      const els = fromEntries(ids.map(id => [id, getEl(id, parentEl) ?? undefined]))
      if (keys(els).every(k => els[k] == elById[k])) return
      setElements(els as any)
    }
    fetchElements()
    const observer = new MutationObserver(mutations => {
      if (!mutations.some(m => m.type == 'childList')) return
      fetchElements()
    })
    observer.observe(parentEl, {childList: true, subtree: true})
    return () => observer.disconnect()
  }, [ids])
  return {elById, ref}
}
